import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { UnsubscribeOutlined, CheckCircleOutline } from '@mui/icons-material';
import axios from 'axios';
import { ENDPOINTS } from '../config';

const Unsubscribe = () => {
  const location = useLocation();
  const history = useHistory();
  const venueId = new URLSearchParams(location.search).get('id');

  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [venueName, setVenueName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Unsubscribe | Piatto';
  }, []);

  const handleUnsubscribe = async () => {
    try {
      setStatus('loading');
      setError(null);

      const response = await axios.post(
        ENDPOINTS.handleUnsubscribe,
        { venueId }
      );

      setVenueName(response.data.venue?.name || '');
      setStatus('success');
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Failed to process your request. Please contact hello@piatto.com.au.'
      );
      setStatus('error');
    }
  };

  if (!venueId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
        <Alert severity="error" role="alert">
          Invalid unsubscribe link. Please contact{' '}
          <a href="mailto:hello@piatto.com.au" style={{ color: 'inherit' }}>
            hello@piatto.com.au
          </a>{' '}
          for assistance.
        </Alert>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8, textAlign: 'center' }}>
        <CheckCircleOutline
          sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          You've been unsubscribed
        </Typography>
        {venueName && (
          <Typography color="text.secondary" paragraph>
            <strong>{venueName}</strong> will no longer receive Piatto emails.
          </Typography>
        )}
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Changed your mind?{' '}
          <a
            href="mailto:hello@piatto.com.au"
            style={{ color: '#B91C1C' }}
            aria-label="Email Piatto to re-subscribe"
          >
            Contact us
          </a>{' '}
          to re-subscribe.
        </Typography>
        <Button variant="outlined" onClick={() => history.push('/')}>
          Back to Piatto
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8, textAlign: 'center' }}>
      <UnsubscribeOutlined
        sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
        aria-hidden="true"
      />
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Unsubscribe from Piatto emails
      </Typography>
      <Typography color="text.secondary" paragraph sx={{ mb: 4 }}>
        Click below to stop receiving listing and claim emails from Piatto for this venue.
      </Typography>

      {status === 'error' && (
        <Alert severity="error" role="alert" sx={{ mb: 3, textAlign: 'left' }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        onClick={handleUnsubscribe}
        disabled={status === 'loading'}
        aria-busy={status === 'loading'}
        sx={{ py: 1.5, px: 4, fontSize: '1rem', mb: 3 }}
      >
        {status === 'loading' ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            Processing...
          </>
        ) : (
          'Confirm Unsubscribe'
        )}
      </Button>

      <Typography variant="body2" color="text.secondary">
        Questions?{' '}
        <a
          href="mailto:hello@piatto.com.au"
          style={{ color: '#B91C1C' }}
          aria-label="Contact Piatto support by email"
        >
          hello@piatto.com.au
        </a>
      </Typography>
    </Container>
  );
};

export default Unsubscribe;
